import { UserError } from '../errors';
import type { ToolOutputImage } from '../types/protocol';
import type { Entry } from './entries';
import type { Manifest } from './manifest';
import type { Snapshot } from './snapshot';

export type ExposedPortScheme = 'http' | 'ws';

export type WorkspaceArchiveData = string | ArrayBuffer | Uint8Array;

export type MaterializeEntryArgs = {
  path: string;
  entry: Entry;
  runAs?: string;
};

export type SandboxDirectoryEntry = {
  name: string;
  path: string;
  type: 'file' | 'dir' | 'other';
};

export type ListDirectoryArgs = {
  path: string;
  runAs?: string;
};

export type ReadFileArgs = {
  path: string;
  runAs?: string;
  maxBytes?: number;
};

export type ReadTextFileArgs = {
  path: string;
  runAs?: string;
  offset?: number;
  limit?: number;
  maxBytes?: number;
};

export type ReadTextFileResult = {
  path: string;
  content: string;
  startLine: number;
  lineCount: number;
  totalLines: number;
  totalBytes: number;
  readBytes: number;
  mtimeMs: number;
};

export type GrepOutputMode = 'content' | 'files_with_matches' | 'count';

export type GrepFilesArgs = {
  pattern: string;
  path?: string;
  glob?: string;
  outputMode?: GrepOutputMode;
  beforeContext?: number;
  afterContext?: number;
  context?: number;
  showLineNumbers?: boolean;
  caseInsensitive?: boolean;
  type?: string;
  headLimit?: number;
  offset?: number;
  multiline?: boolean;
  runAs?: string;
};

export type GrepFilesResult = {
  mode: GrepOutputMode;
  numFiles: number;
  filenames: string[];
  content?: string;
  numLines?: number;
  numMatches?: number;
  appliedLimit?: number;
  appliedOffset?: number;
};

export type GlobFilesArgs = {
  pattern: string;
  path?: string;
  limit?: number;
  runAs?: string;
};

export type GlobFilesResult = {
  durationMs: number;
  numFiles: number;
  filenames: string[];
  truncated: boolean;
};

export type StatFileArgs = {
  path: string;
  runAs?: string;
};

export type StatFileResult = {
  path: string;
  exists: boolean;
  isFile: boolean;
  size: number;
  mtimeMs: number;
};

export type WriteFileArgs = {
  path: string;
  content: string;
  runAs?: string;
};

export type WriteFileResult = {
  path: string;
  type: 'create' | 'update';
  bytesWritten: number;
};

export type EditFileArgs = {
  path: string;
  oldString: string;
  newString: string;
  replaceAll?: boolean;
  runAs?: string;
};

export type EditFileResult = {
  path: string;
  replacements: number;
};

export type ViewImageArgs = {
  path: string;
  runAs?: string;
  maxBytes?: number;
};

export type WriteStdinArgs = {
  sessionId: number;
  chars?: string;
  yieldTimeMs?: number;
  maxOutputTokens?: number;
};

export type SandboxExecResult = {
  output: string;
  stdout: string;
  stderr: string;
  wallTimeSeconds: number;
  exitCode?: number | null;
  sessionId?: number;
  originalTokenCount?: number;
  outputFilePath?: string;
  outputMetadataPath?: string;
  outputFileSizeBytes?: number;
  outputPreviewBytes?: number;
  outputTruncated?: boolean;
};

export type ExecCommandArgs = {
  cmd: string;
  workdir?: string;
  shell?: string;
  login?: boolean;
  tty?: boolean;
  yieldTimeMs?: number;
  maxOutputTokens?: number;
  runAs?: string;
};

export type ExecForegroundArgs = {
  cmd: string;
  workdir?: string;
  shell?: string;
  login?: boolean;
  timeoutMs?: number;
  maxOutputTokens?: number;
  outputPreviewBytes?: number;
  outputId?: string;
  runAs?: string;
};

/**
 * 声明 args 中某个下标是一条路径参数,需要被解析并约束在工作区内(逃逸即抛错)。
 * forWrite 标记该路径是写入目标(用于只读 path grant 的判定)。
 */
export type ExecForegroundArgvPathArg = {
  index: number;
  forWrite?: boolean;
};

/**
 * 直接以 argv 形式执行单个二进制,**不经过 shell**(无分词、无展开、无元字符)。
 * 这是受限白名单执行器(run_command)的执行通道:安全性靠调用方对 command/args
 * 的逐条校验,本方法只负责把已声明的 path 参数解析+约束在工作区内,再 spawn。
 */
export type ExecForegroundArgvArgs = {
  command: string;
  args: string[];
  pathArgs?: ExecForegroundArgvPathArg[];
  workdir?: string;
  timeoutMs?: number;
  maxOutputTokens?: number;
  outputPreviewBytes?: number;
  outputId?: string;
  runAs?: string;
};

export type ExposedPortEndpoint = {
  host: string;
  port: number;
  tls?: boolean;
  query?: string;
  protocol?: string;
  url?: string;
  [key: string]: unknown;
};

export interface SandboxSessionState {
  manifest: Manifest;
  snapshot?: Snapshot | null;
  snapshotFingerprint?: string | null;
  snapshotFingerprintVersion?: string | null;
  workspaceReady?: boolean;
  environment?: Record<string, string>;
  exposedPorts?: Record<string, ExposedPortEndpoint>;
  [key: string]: unknown;
}

export type SandboxSessionLifecycleOptions = {
  reason?: string;
  [key: string]: unknown;
};

export type SandboxPreStopHook = () => Promise<void> | void;

export interface SandboxSession<
  TState extends SandboxSessionState = SandboxSessionState,
> {
  state: TState;
  start?(options?: SandboxSessionLifecycleOptions): Promise<void>;
  running?(): Promise<boolean>;
  registerPreStopHook?(hook: SandboxPreStopHook): (() => void) | void;
  runPreStopHooks?(): Promise<void>;
  preStop?(options?: SandboxSessionLifecycleOptions): Promise<void>;
  stop?(options?: SandboxSessionLifecycleOptions): Promise<void>;
  shutdown?(options?: SandboxSessionLifecycleOptions): Promise<void>;
  delete?(options?: SandboxSessionLifecycleOptions): Promise<void>;
  exec?(args: ExecCommandArgs): Promise<SandboxExecResult>;
  execCommand?(args: ExecCommandArgs): Promise<string>;
  execForeground?(args: ExecForegroundArgs): Promise<SandboxExecResult>;
  execForegroundArgv?(args: ExecForegroundArgvArgs): Promise<SandboxExecResult>;
  writeStdin?(args: WriteStdinArgs): Promise<string>;
  viewImage?(args: ViewImageArgs): Promise<ToolOutputImage>;
  readFile?(args: ReadFileArgs): Promise<string | Uint8Array>;
  readTextFile?(args: ReadTextFileArgs): Promise<ReadTextFileResult>;
  grepFiles?(args: GrepFilesArgs): Promise<GrepFilesResult>;
  globFiles?(args: GlobFilesArgs): Promise<GlobFilesResult>;
  statFile?(args: StatFileArgs): Promise<StatFileResult>;
  writeFile?(args: WriteFileArgs): Promise<WriteFileResult>;
  editFile?(args: EditFileArgs): Promise<EditFileResult>;
  listDir?(args: ListDirectoryArgs): Promise<SandboxDirectoryEntry[]>;
  pathExists?(path: string, runAs?: string): Promise<boolean>;
  materializeEntry?(args: MaterializeEntryArgs): Promise<void>;
  applyManifest?(manifest: Manifest, runAs?: string): Promise<void>;
  persistWorkspace?(): Promise<Uint8Array>;
  hydrateWorkspace?(data: WorkspaceArchiveData): Promise<void>;
  resolveExposedPort?(port: number): Promise<ExposedPortEndpoint>;
  supportsPty?(): boolean;
  close?(): Promise<void>;
}

export type SandboxSessionLike<
  TState extends SandboxSessionState = SandboxSessionState,
> = SandboxSession<TState>;

export const SANDBOX_SESSION_STATE_VERSION = 1 as const;

export type SandboxSessionStateEnvelope = {
  version: typeof SANDBOX_SESSION_STATE_VERSION;
  backendId: string;
  manifest: Record<string, unknown>;
  snapshot?: Snapshot | null;
  snapshotFingerprint?: string | null;
  snapshotFingerprintVersion?: string | null;
  workspaceReady: boolean;
  exposedPorts?: Record<string, ExposedPortEndpoint>;
  providerState: Record<string, unknown>;
};

export function normalizeExposedPort(port: number): number {
  if (!Number.isInteger(port) || port < 1 || port > 65_535) {
    throw new UserError('Exposed ports must be integers between 1 and 65535.');
  }
  return port;
}

export function recordExposedPortEndpoint(
  state: SandboxSessionState,
  endpoint: ExposedPortEndpoint,
  keyPort: number = endpoint.port,
): ExposedPortEndpoint {
  const normalizedEndpoint: ExposedPortEndpoint = {
    ...endpoint,
    port: normalizeExposedPort(endpoint.port),
    tls: endpoint.tls ?? false,
    query: endpoint.query ?? '',
  };
  state.exposedPorts = {
    ...(state.exposedPorts ?? {}),
    [exposedPortStateKey(keyPort)]: normalizedEndpoint,
  };
  return normalizedEndpoint;
}

export function exposedPortStateKey(port: number): string {
  return String(normalizeExposedPort(port));
}

export function getRecordedExposedPortEndpoint(
  state: SandboxSessionState,
  port: number,
): ExposedPortEndpoint | undefined {
  return state.exposedPorts?.[exposedPortStateKey(port)];
}

export function urlForExposedPort(
  endpoint: ExposedPortEndpoint,
  scheme: ExposedPortScheme,
): string {
  if (scheme !== 'http' && scheme !== 'ws') {
    throw new UserError('Exposed port URL schemes must be "http" or "ws".');
  }

  const tls = endpoint.tls ?? false;
  let prefix: string;
  if (scheme === 'http') {
    prefix = tls ? 'https' : 'http';
  } else {
    prefix = tls ? 'wss' : 'ws';
  }
  const defaultPort = tls ? 443 : 80;
  const host =
    endpoint.host.includes(':') && !endpoint.host.startsWith('[')
      ? `[${endpoint.host}]`
      : endpoint.host;
  const base =
    endpoint.port === defaultPort
      ? `${prefix}://${host}/`
      : `${prefix}://${host}:${endpoint.port}/`;
  const query = endpoint.query ?? '';
  return query ? `${base}?${query}` : base;
}
